import { InvalidEvent } from "react";
import { Orderable } from "../types/common";

export const isEmptyObject = (obj: any): boolean => !obj || Object.getOwnPropertyNames(obj).length === 0;

export const parseObject = <T>(obj?: object): T => {
  if (!obj) return {} as T;

  // remove props for internal use, eg. __typename added by Apollo client
  const newObj = { ...obj } as Record<string, any>;
  Object.keys(newObj)
    .filter(key => key.startsWith('_'))
    .forEach(key => delete newObj[key]);

  return newObj as T;
};

export const getFormDataObject = <T>(form: HTMLFormElement): T => Object.fromEntries(new FormData(form)) as T;

export const trim = (value: any) => value !== undefined && value !== null ? String(value).trim() : value;
export const trimToEmpty = (value: any) => trim(value) ?? '';
export const isBlank = (value: any) => trimToEmpty(value).length === 0;

export const capitalize = (str?: string) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

export const castToNumber = (value: any) => {
  const n = +value;
  return isNaN(n) ? undefined : n;
};

export const toNumber = (value: any, defaultValue: number) => castToNumber(value) ?? defaultValue;

export const isPositiveNumber = (value: number | undefined) => value && value > 0;

export const validEmail = (value: string) => {
  const emailRegex = /^[A-Z0-9._%+\-']+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
  return emailRegex.test(value)
}

export const validURL = (value: string) => {
  try {
    const parsedURL: URL = !value.toLowerCase().startsWith("http") ? new URL("http://" + value) : new URL(value)

    // Check TLD
    if (parsedURL.hostname) {
      if (parsedURL.hostname.includes('.') && /[A-Za-z]{2,}$/.test(parsedURL.hostname.split(".").pop()!)) {
        return true;
      }
    }
  } catch (error) {
    return false
  }
  return false
}

export const validNumber = (value: string, maxLength: number) => {
  if (value.length > maxLength) return false
  const numRegex = /^\d+$/
  return numRegex.test(value)
}

export const numberOnly = (value: string) => value.trim().replace(/\D/g, '')

export const validOrgName = (value: string) => {
  // as of 2024-10-18, . is now an allowed symbol in org names / headers
  const nameRegex = /^[A-Za-zÀ-ÿ0-9\s&()+/_\-,\.']+$/
  return nameRegex.test(value)
}

export const validOrgNameErrorMessage = "Only the following special characters are allowed: & ( ) + / _ - ' , .";

export const join = (str1: string | undefined, str2: string | undefined) => {
  return ((str1 || '') + ' ' + (str2 || '')).trim()
}

export const equals = (a: any, b: any) => String(a) === String(b);

export const deepEqual = (object1: object, object2: object): boolean => {
  const keys1: string[] = Object.keys(object1);
  const keys2: string[] = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key as keyof typeof object1];
    const val2 = object2[key as keyof typeof object2];
    const areObjects = isObject(val1) && isObject(val2);

    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

export const isObject = (object: object) => {
  return object != null && typeof object === 'object' && !Array.isArray(object);
}

export const arrayMove = <T>(array: T[], from: number, to: number): T[] => {
  const newArray = [...array];
  const [item] = newArray.splice(from, 1);
  newArray.splice(to, 0, item);
  return newArray;
}

export const toOrdered = <T extends Orderable>(data: T[] | undefined, comparator: (item: T) => number) =>
  data?.map(item => ({
    ...item,
    displayOrder: comparator(item)
  } as T))
    .sort((a, b) => {
      if (a.displayOrder === b.displayOrder) {
        return 0;
      } else if (a.displayOrder < b.displayOrder) {
        return -1;
      } else {
        return 1;
      }
    });

export const parseCommonError = (event: InvalidEvent<HTMLInputElement>) => {
  const { value, type, ariaLabel, validity } = event.target;

  if (validity.customError) {
    return event.target.validationMessage;
  } else if (validity.valueMissing) {
    return `${ariaLabel ?? 'This field'} is required.`;
  } else if (validity.tooShort) {
    return `Please enter at least ${event.target.minLength} characters.`;
  } else if (type === 'email') {
    return 'Invalid email format.';
  }

  const msg = `Invalid ${ariaLabel?.toLowerCase() ?? 'value'}.`;

  if (validity.patternMismatch && event.target.inputMode === 'numeric') {
    return `${msg} ${castToNumber(value) ? 'Please enter an integer number.' : 'Please enter a number.'}`;
  }

  return msg;
};

export const normalizeApostrophes = (str?: string) => str ? str.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"') : "";

export const normalizeWhiteSpaces= (str?: string) => str? str.trim().replace(/\s+/g, " ") : "";


